import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { PortalBrandingOut as PortalBranding } from '../types/apiRequests';
import { config } from '../config';

const DEFAULT_BRANDING: PortalBranding = {
  primary_color: null,
  secondary_color: null,
  logo_url: null,
  favicon_url: null,
  portal_title: null,
  login_background_color: null,
  header_color: null,
  header_text_color: null,
  background_color: null,
  font_family: null,
  border_radius: null,
  login_subtitle: null,
};

const CACHE_KEY = 'portal_branding';

function getCachedBranding(): PortalBranding {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch { /* ignore */ }
  return DEFAULT_BRANDING;
}

const BrandingContext = createContext<PortalBranding>(DEFAULT_BRANDING);

export function useBranding(): PortalBranding {
  return useContext(BrandingContext);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PortalBranding>(getCachedBranding);
  const [loaded, setLoaded] = useState(() => sessionStorage.getItem(CACHE_KEY) !== null);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    fetch(`${config.api.baseUrl}/portal/branding`, { signal: controller.signal })
      .then(res => (res.ok ? res.json() : DEFAULT_BRANDING))
      .then((data: PortalBranding) => {
        setBranding(data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));

        if (data.portal_title) {
          document.title = data.portal_title;
        }
        if (data.favicon_url) {
          let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = data.favicon_url;
        }
        if (data.font_family) {
          const fontName = encodeURIComponent(data.font_family);
          let fontLink = document.querySelector<HTMLLinkElement>('link[data-google-font]');
          if (!fontLink) {
            fontLink = document.createElement('link');
            fontLink.rel = 'stylesheet';
            fontLink.setAttribute('data-google-font', 'true');
            document.head.appendChild(fontLink);
          }
          fontLink.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
        }
      })
      .catch(e => {
        if (e.name !== 'AbortError') {
          console.error('Failed to load branding:', e);
        }
      })
      .finally(() => {
        setLoaded(true);
        clearTimeout(timeoutId);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, []);

  if (!loaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}
